import { useEffect, useState } from "react";
import { ShoppingBag, ChevronDown, Send, ShieldCheck, Mail, MessageSquareText, CheckCircle2, Download } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const PAY_LABELS = { zelle: "Zelle", cashapp: "Cash App", venmo: "Venmo", paypal: "PayPal", credit_card: "Credit card" };

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [sending, setSending] = useState(null);

  const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const resend = async (o) => {
    setSending(o.id);
    try {
      const r = await api.post(`/orders/${o.id}/resend-code`);
      toast.success(`Access code sent to ${r.data.contact} by ${r.data.delivery_method === "email" ? "email" : "text"}.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send the code.");
    } finally { setSending(null); }
  };

  const counts = {
    total: orders.length,
    pending: orders.filter((o) => o.status !== "code_sent").length,
    sent: orders.filter((o) => o.status === "code_sent").length,
  };

  return (
    <div data-testid="orders-page">
      <span className="overline text-brand-terracotta">Add-Ons</span>
      <h1 className="font-display text-4xl tracking-tight mt-1 mb-6">Orders & Waivers</h1>

      <div className="grid grid-cols-3 gap-4 mb-8">
        {[["Total", counts.total], ["Awaiting code", counts.pending], ["Code sent", counts.sent]].map(([label, val]) => (
          <div key={label} className="bg-white rounded-2xl p-5 border border-black/5 shadow-sm" data-testid={`order-stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <div className="overline text-brand-forest/50">{label}</div>
            <div className="font-display text-3xl mt-1">{val}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden">
        {orders.length === 0 ? (
          <div className="p-10 text-center text-brand-forest/50 flex flex-col items-center gap-2">
            <ShoppingBag size={28} /> No orders yet.
          </div>
        ) : (
          <ul className="divide-y divide-black/5">
            {orders.map((o) => {
              const isOpen = openId === o.id;
              const sent = o.status === "code_sent";
              return (
                <li key={o.id} data-testid={`order-row-${o.id}`}>
                  <button
                    type="button"
                    onClick={() => setOpenId(isOpen ? null : o.id)}
                    data-testid={`order-toggle-${o.id}`}
                    className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-brand-bg/60 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-brand-forest truncate">{o.guest_name}</span>
                        <span className="text-brand-forest/40">·</span>
                        <span className="text-brand-forest/70 truncate">{o.addon_title || "Add-On"}</span>
                        {o.waiver_agreed && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 bg-green-100 rounded-full px-2 py-0.5">
                            <ShieldCheck size={12} /> Waiver signed
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-brand-forest/50 mt-0.5">{fmt(o.created_at)} · {PAY_LABELS[o.payment_method] || o.payment_method}</div>
                    </div>
                    <span className={`text-xs font-semibold rounded-full px-2.5 py-1 shrink-0 ${sent ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                      {sent ? "Code sent" : "Awaiting"}
                    </span>
                    <ChevronDown size={18} className={`text-brand-forest/40 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-5 pt-1 bg-brand-bg/40" data-testid={`order-detail-${o.id}`}>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="rounded-xl bg-white border border-black/5 p-4 space-y-1.5 text-sm">
                          <div className="overline text-brand-forest/40 mb-1">Order</div>
                          <p><span className="text-brand-forest/50">Add-on:</span> <span className="font-medium">{o.addon_title || "—"}</span></p>
                          <p><span className="text-brand-forest/50">Price:</span> {o.price_note || "—"}</p>
                          <p><span className="text-brand-forest/50">Payment:</span> {PAY_LABELS[o.payment_method] || o.payment_method}</p>
                          <p className="flex items-center gap-1.5">
                            <span className="text-brand-forest/50">Deliver by:</span>
                            {o.delivery_method === "email" ? <><Mail size={13} /> Email</> : <><MessageSquareText size={13} /> Text</>}
                          </p>
                          <p className="break-all"><span className="text-brand-forest/50">Contact:</span> {o.contact}</p>
                          {sent && <p className="text-green-700 flex items-center gap-1.5"><CheckCircle2 size={13} /> Code sent {fmt(o.code_sent_at)}</p>}
                        </div>

                        <div className="rounded-xl bg-white border border-black/5 p-4 space-y-1.5 text-sm" data-testid={`order-waiver-${o.id}`}>
                          <div className="overline text-brand-forest/40 mb-1 flex items-center gap-1.5"><ShieldCheck size={13} className="text-green-600" /> Signed Waiver</div>
                          {o.waiver_agreed ? (
                            <>
                              <p><span className="text-brand-forest/50">Legal name:</span> <span className="font-medium">{o.waiver_legal_name || "—"}</span></p>
                              <p><span className="text-brand-forest/50">Signature:</span> <span style={{ fontFamily: "cursive" }} className="text-base">{o.waiver_signature || "—"}</span></p>
                              <p><span className="text-brand-forest/50">Signed:</span> {fmt(o.waiver_signed_at)}</p>
                              <p className="text-green-700 flex items-center gap-1.5"><CheckCircle2 size={13} /> Agreed to Adventure Pack waiver</p>
                              {o.waiver_pdf_url && (
                                <a
                                  href={o.waiver_pdf_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  data-testid={`order-waiver-pdf-${o.id}`}
                                  className="inline-flex items-center gap-1.5 mt-1 text-sm font-semibold text-brand-terracotta hover:underline"
                                >
                                  <Download size={14} /> Download signed waiver (PDF)
                                </a>
                              )}
                            </>
                          ) : (
                            <p className="text-brand-forest/50 italic">No waiver on record (order predates the waiver feature).</p>
                          )}
                        </div>
                      </div>

                      <Button
                        onClick={() => resend(o)}
                        disabled={sending === o.id}
                        data-testid={`order-resend-${o.id}`}
                        className="mt-4 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white transition-colors"
                      >
                        <Send size={15} className={`mr-2 ${sending === o.id ? "animate-pulse" : ""}`} />
                        {sending === o.id ? "Sending…" : sent ? "Re-send access code" : "Send access code"}
                      </Button>
                      <p className="text-xs text-brand-forest/50 mt-2">Only send after you've confirmed payment. Make sure the add-on has an access code set in <strong>Add-Ons</strong>.</p>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
